/**
 * @fileoverview The organisation details dialog.
 * @description Opened from My Page's header. Reads the organisation's legal, billing and bank details
 * (GET /sales/organisation-details), the record Send Payment Details emails to a bank, and shows them
 * read-only. A Super Admin (`canEdit`) can edit them, or create them when they were never saved (the
 * backend answers 409/4120 until then); anyone else is told they have not been set up. The backend
 * decides who may read and who may save, so a refusal shows as the dialog's error. The page mounts
 * it only while it is open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIsMutating } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useOrganisationDetails } from '@/features/sales/hooks/useOrganisationDetails'
import { ORGANISATION_SAVE_MUTATION_KEY } from '@/features/sales/hooks/useSaveOrganisationDetails'
import { ApiException } from '@/shared/api/api.exception'
import Button from '@/shared/components/atoms/Button'
import Skeleton from '@/shared/components/atoms/Skeleton'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'
import { toTestIdSegment } from '@/shared/lib/test-id/test-id.utils'

import OrganisationDetailsForm from './OrganisationDetailsForm'
import SalesRequestError from './SalesRequestError'

import type { OrganisationDetails } from '@/features/sales/types/my-page.types'

interface OrganisationDetailsModalProps {
  canEdit: boolean
  onClose: () => void
}

/**
 * @description One labelled value in the read-only view.
 * @param {{ label: string; value: string | null; testId: string }} props - The label and value.
 * @param {string} props.label - The field's label.
 * @param {string | null} props.value - Its value, or null when not set.
 * @param {string} props.testId - QA locator; the value gets `<testId>-value`.
 */
function Row({ label, value, testId }: { label: string; value: string | null; testId: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      <span className="text-[10.5px] font-semibold tracking-[0.7px] text-footer">{label}</span>
      <span
        className="text-[13px] font-medium break-words text-ink"
        data-testid={`${testId}-value`}
      >
        {value ?? '—'}
      </span>
    </div>
  )
}

/**
 * @description The saved details, read-only.
 * @param {{ details: OrganisationDetails }} props - The details.
 * @param {OrganisationDetails} props.details - The saved details.
 */
function DetailsView({ details }: { details: OrganisationDetails }) {
  const { t } = useTranslation()
  const f = 'sales.myWork.organisation.fields'
  const b = 'sales.myWork.organisation.bank'
  const general = [
    'legalName',
    'tradeName',
    'cin',
    'gstin',
    'pan',
    'registeredAddress',
    'city',
    'state',
    'pincode',
    'billingContactName',
    'billingEmail',
    'billingPhone',
    'supportEmail',
  ] as const

  return (
    <div className="flex flex-col gap-5" data-testid="organisation-details-view">
      <div className="grid grid-cols-1 gap-3.5 rounded-lg border border-row-border bg-surface-hover p-4 sm:grid-cols-2">
        {general.map((name) => (
          <Row
            key={name}
            label={t(`${f}.${name}`)}
            value={details[name]}
            testId={`organisation-${toTestIdSegment(name)}`}
          />
        ))}
      </div>
      <span className="text-[11px] font-bold tracking-[0.8px] text-kicker">
        {t('sales.myWork.organisation.bankTitle')}
      </span>
      <div className="grid grid-cols-1 gap-3.5 rounded-lg border border-row-border bg-surface-hover p-4 sm:grid-cols-2">
        <Row
          label={t(`${b}.beneficiaryName`)}
          value={details.bank.beneficiaryName}
          testId="organisation-bank-beneficiary-name"
        />
        <Row
          label={t(`${b}.bankName`)}
          value={details.bank.bankName}
          testId="organisation-bank-bank-name"
        />
        <Row
          label={t(`${b}.branch`)}
          value={details.bank.branch}
          testId="organisation-bank-branch"
        />
        <Row
          label={t(`${b}.accountNumber`)}
          value={details.bank.accountNumber}
          testId="organisation-bank-account-number"
        />
        <Row label={t(`${b}.ifsc`)} value={details.bank.ifsc} testId="organisation-bank-ifsc" />
        <Row
          label={t(`${b}.accountType`)}
          value={t(`sales.myWork.organisation.accountTypes.${details.bank.accountType}`)}
          testId="organisation-bank-account-type"
        />
      </div>
    </div>
  )
}

/**
 * @description Whether the details read failed because nothing has been saved yet (a 409).
 * @param {boolean} isError - Whether the read failed.
 * @param {unknown} error - The read's error.
 * @returns {boolean} True when the organisation record has never been saved.
 */
function isNeverSaved(isError: boolean, error: unknown): boolean {
  return isError && error instanceof ApiException && error.status === 409
}

/**
 * @component
 * @param {OrganisationDetailsModalProps} props - Whether the viewer may edit, and the close handler.
 * @param {boolean} props.canEdit - Whether to offer editing (a Super Admin).
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 */
function OrganisationDetailsModal({ canEdit, onClose }: OrganisationDetailsModalProps) {
  const { t } = useTranslation()
  const query = useOrganisationDetails(true)
  const saving = useIsMutating({ mutationKey: ORGANISATION_SAVE_MUTATION_KEY }) > 0
  const guardedClose = useGuardedClose(onClose, saving)
  // The details the form was opened with. A background refetch must not swap the version a save is
  // sent against while the user is still typing, so the form keeps this snapshot, not `query.data`.
  const [openedWith, setOpenedWith] = useState<OrganisationDetails | null | undefined>(undefined)
  const neverSaved = isNeverSaved(query.isError, query.error)
  const showForm = canEdit && (openedWith !== undefined || neverSaved)

  /**
   * @description Opens the form on the details showing right now.
   */
  const openForm = () => setOpenedWith(query.data ?? null)

  /**
   * @description Returns to the read-only view.
   */
  const closeForm = () => setOpenedWith(undefined)

  /**
   * @description Leaves the form: back to the read-only view, or closes when nothing was saved yet.
   */
  const leaveForm = () => {
    if (neverSaved) {
      onClose()
      return
    }
    closeForm()
  }

  let body
  if (query.isPending) {
    body = (
      <div className="flex flex-col gap-3" data-testid="organisation-details-loading">
        <Skeleton height={20} width="60%" />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </div>
    )
  } else if (showForm) {
    body = (
      <OrganisationDetailsForm
        details={openedWith ?? null}
        onSaved={closeForm}
        onCancel={leaveForm}
      />
    )
  } else if (neverSaved) {
    body = (
      <p className="text-sm text-slate" data-testid="organisation-details-not-saved">
        {t('sales.myWork.organisation.notSaved')}
      </p>
    )
  } else if (query.isError) {
    body = <SalesRequestError error={query.error} />
  } else {
    body = (
      <div className="flex flex-col gap-5">
        <DetailsView details={query.data} />
        {canEdit && (
          <div className="w-fit">
            <Button
              type="button"
              size="md"
              variant="secondary"
              onClick={openForm}
              data-testid="organisation-details-edit-button"
            >
              {t('sales.myWork.organisation.edit')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      open
      onClose={guardedClose}
      data-testid="organisation-details-modal"
      title={t('sales.myWork.organisation.title')}
      subtitle={t('sales.myWork.organisation.subtitle')}
      size="lg"
    >
      {body}
    </Modal>
  )
}

export default OrganisationDetailsModal
